import type { Dictionary } from "../types";

/** Spanish (España). Typed against `en`, so every key must be present. */
export const es: Dictionary = {
  common: {
    appName: "Retrospecter",
    poweredBy: "Con tecnología de",
    loading: "Cargando…",
    cancel: "Cancelar",
    save: "Guardar",
    saving: "Guardando…",
    returnHome: "Volver al inicio",
    yours: "Tuya",
    by: "de {name}",
  },

  nav: {
    logOut: "Cerrar sesión",
    switchToDark: "Cambiar al tema oscuro",
    switchToLight: "Cambiar al tema claro",
    darkTheme: "Tema oscuro",
    lightTheme: "Tema claro",
    language: "Idioma",
  },

  login: {
    title: "Iniciar sesión",
    subtitle: "Acceso del personal a las retrospectivas de Retrospecter",
    redirectedNotice:
      "Esta retrospectiva solo está disponible para el personal. Inicia sesión para continuar.",
    username: "Correo o usuario",
    usernamePlaceholder: "nombre@empresa.com",
    password: "Contraseña",
    submit: "Entrar",
    submitting: "Entrando…",
    invalidCredentials: "Usuario o contraseña incorrectos.",
    providerUnavailable:
      "El servicio de autenticación no está disponible. Vuelve a intentarlo en unos minutos.",
    anonymousHint:
      "Las retrospectivas en curso están abiertas a cualquiera con el enlace: no hace falta iniciar sesión.",
  },

  home: {
    title: "Retrospectivas",
    subtitle: "Empieza una sesión nueva o reabre una que ya haya hecho tu equipo.",
    newRetro: "Nueva retrospectiva",
    nameLabel: "Nombre de la retrospectiva",
    namePlaceholder: "p. ej. Retro del Sprint 42",
    create: "Crear retrospectiva",
    creating: "Creando…",
    createError: "No se pudo crear la retrospectiva.",
    draftHint:
      "Las retrospectivas nuevas empiezan como borrador privado. Actívala para compartir el enlace con tu equipo.",
  },

  create: {
    title: "Nueva retrospectiva",
    subtitle: "Ponle nombre a la sesión y elige el formato con el que trabajará tu equipo.",
    back: "Volver a las retrospectivas",
    chooseTemplate: "Elige un formato",
    chooseHint: "Cada vista previa muestra las columnas que verá tu equipo.",
    selected: "Seleccionado",
    columnCount: { one: "{count} columna", other: "{count} columnas" },
    loadError: "No se han podido cargar las plantillas. Actualiza para reintentarlo.",
    error: "No se ha podido crear la retrospectiva.",
  },

  list: {
    heading: "Tus tableros",
    regionLabel: "Retrospectivas",
    filterLabel: "Filtrar retrospectivas",
    filterAll: "Todas",
    filterPast: "Pasadas",
    filterRunning: "En curso",
    filterDrafts: "Borradores",
    empty: "Todavía no hay retrospectivas: crea la primera arriba.",
    emptyFiltered: "Aquí no hay nada todavía.",
    loadError: "No se pudieron cargar las retrospectivas. Actualiza para reintentar.",
    createdOn: "Creada el {date}",
    completedOn: "Completada el {date}",
    notes: { one: "{count} nota", other: "{count} notas" },
    participants: { one: "{count} participante", other: "{count} participantes" },
  },

  board: {
    loading: "Cargando la retrospectiva…",
    accessDeniedTitle: "Acceso denegado",
    accessDeniedBody:
      "Has iniciado sesión, pero no tienes permiso para ver esta retrospectiva.",
    notFoundTitle: "No encontrada",
    notFoundBody: "La retrospectiva que buscas no existe.",
    activate: "Activar",
    activating: "Activando…",
    complete: "Completar",
    completing: "Completando…",
    joinPrompt: "Únete para añadir notas, votar y reorganizar el tablero.",
    join: "Unirse a la retrospectiva",
    joining: "Uniéndose…",
    joinError: "No se pudo unir a esta retrospectiva.",
    draftNotice:
      "Esta retrospectiva sigue siendo un borrador: solo tú puedes verla. Actívala para compartir el enlace con tu equipo.",
    dragHint:
      "Consejo: arrastra los post-its donde quieras, tira de una esquina para cambiar su tamaño y posa uno sobre otro para agruparlos.",
    readOnly: "Esta retrospectiva se ha completado y ahora es de solo lectura",
    participantsRegion: "Participantes",
    participantsHeading: "Participantes ({count})",
    noParticipants: "Todavía no se ha unido nadie.",
    statusDraft: "BORRADOR",
    statusActive: "ACTIVA",
    statusCompleted: "COMPLETADA",
  },

  column: {
    empty: "Aquí no hay nada todavía: suelta una nota donde quieras",
    pileLabel: "Coge una nota del taco",
    pileHint: "Arrastra una nota a una columna, o pulsa Intro para elegirla.",
    pileChoose: "Añadir una nota a {column}",
    dropHere: "Suelta para añadir una nota",
    addNote: "Añadir nota",
    addNoteTo: "Añadir nota a {column}",
    newNoteFor: "Nueva nota para {column}",
    notePlaceholder: "Comparte lo que piensas…",
    saveNote: "Guardar nota",
    adding: "Añadiendo…",
    cancelNote: "Cancelar nota",
  },

  note: {
    yourNote: "Tú",
    react: "Reaccionar con {emoji}",
    unreact: "Quitar tu reacción {emoji}",
    addReaction: "Añadir una reacción",
    edit: "Editar",
    editNote: "Editar nota",
    delete: "Eliminar",
    deleteNote: "Eliminar nota",
    vote: "Votar esta nota",
    unvote: "Quitar tu voto",
    voteHint: "Únete a la retrospectiva para votar",
    move:
      "Mover nota. Las flechas la desplazan por la columna; con Mayús la envían a la columna siguiente.",
    moveHint: "Arrastra, o enfoca y usa las flechas",
    resize: "Cambiar el tamaño de la nota. Usa las flechas para ajustarlo.",
    resizeHint: "Arrastra la esquina, o enfoca y usa las flechas",
    draggable: "Nota arrastrable",
    saveError: "No se pudo guardar el cambio.",
    deleteError: "No se pudo eliminar la nota.",
    voteError: "No se pudo registrar tu voto.",
    movedToColumn: "Movida a {column}",
    movedTo: "Movida a {x} a la derecha y {y} hacia abajo en {column}",
    groupWithPrevious: "Agrupar con la nota de arriba",
    ungroup: "Sacar del grupo",
    ungroupError: "No se ha podido sacar la nota del grupo.",
    groupError: "No se han podido agrupar las notas.",
    dropToGroup: "Suelta para agrupar",
    groupSummary: "{votes} votos en el grupo",
    groupedNotes: { one: "{count} nota", other: "{count} notas" },
  },

  actions: {
    heading: "Acciones",
    regionLabel: "Acciones acordadas",
    intro: "Lo que el equipo se ha comprometido a hacer.",
    empty: "Todavía no hay nada acordado.",
    textLabel: "¿Qué vamos a hacer?",
    textPlaceholder: "Dividir el pipeline de despliegue en dos",
    ownerLabel: "¿Quién se encarga?",
    ownerPlaceholder: "Nombre (opcional)",
    unassigned: "Sin asignar",
    add: "Añadir",
    adding: "Añadiendo…",
    markDone: "Marcar como hecha",
    markNotDone: "Marcar como pendiente",
    delete: "Eliminar acción",
    saveError: "No se ha podido guardar la acción.",
    carriedHeading: "De la vez anterior",
    carriedIntro: "Sigue pendiente de la retrospectiva anterior.",
    carriedFrom: "De {name}",
    carryOver: "Traer aquí",
    carriedCount: { one: "{count} acción pendiente", other: "{count} acciones pendientes" },
  },

  phase: {
    label: "Fase",
    brainstorm: "Ideas",
    grouping: "Agrupación",
    voting: "Votación",
    discussion: "Debate",
    error: "No se pudo cambiar la fase.",
  },

  timer: {
    label: "Temporizador",
    addMinutes: "+{count} min",
    minutes: "{count} min",
    stop: "Parar",
    finished: "Tiempo agotado",
  },

  notFound: {
    title: "Página no encontrada",
    body: "Vaya, la página que buscas no existe. Volvamos al buen camino.",
  },

  templates: {
    "Start/Stop/Continue": {
      name: "Empezar/Parar/Continuar",
      description: "La opción por defecto. Cambios concretos que hacer, dejar y mantener.",
      columns: { Start: "Empezar", Stop: "Parar", Continue: "Continuar" },
      hints: {
        "Start": "Algo que aún no hacemos y nos vendría bien.",
        "Stop": "Algo que nos cuesta tiempo o ánimo y deberíamos dejar.",
        "Continue": "Lo que ya funciona: nómbralo para no perderlo.",
      },
    },
    "Mad/Sad/Glad": {
      name: "Enfado/Pena/Alegría",
      description: "Saca a la luz cómo se vivió el sprint antes de debatir qué hacer.",
      columns: { Mad: "Enfado", Sad: "Pena", Glad: "Alegría" },
      hints: {
        "Mad": "Lo que te frustró o te enfadó este sprint.",
        "Sad": "Lo que te decepcionó o se quedó corto.",
        "Glad": "Lo que te alegró o salió mejor de lo esperado.",
      },
    },
    "What Went Well": {
      name: "Qué fue bien",
      description: "Una revisión directa que termina en compromisos.",
      columns: {
        "What went well": "Qué fue bien",
        "What didn't": "Qué no fue bien",
        "Action items": "Acciones",
      },
      hints: {
        "What went well": "Logros que merece la pena repetir, por pequeños que sean.",
        "What didn't": "Dónde nos costó: describe el problema, no a la persona.",
        "Action items": "Un paso concreto, a ser posible con responsable.",
      },
    },
    "Rose/Bud/Thorn": {
      name: "Rosa/Brote/Espina",
      description: "Equilibra logros, problemas y promesas incipientes.",
      columns: { Rose: "Rosa", Bud: "Brote", Thorn: "Espina" },
      hints: {
        "Rose": "Un éxito claro o algo digno de celebrar.",
        "Bud": "Una idea incipiente u oportunidad que cultivar.",
        "Thorn": "Un problema o bloqueo que hizo daño este sprint.",
      },
    },
    "4 Ls": {
      name: "Las 4 L",
      description: "Separa lo emocional de lo aprendido. Útil tras un sprint duro.",
      columns: {
        Liked: "Gustó",
        Learned: "Aprendido",
        Lacked: "Faltó",
        "Longed for": "Echamos de menos",
      },
      hints: {
        "Liked": "Lo que te gustó de cómo trabajamos.",
        "Learned": "Algo nuevo que aprendiste o entendiste.",
        "Lacked": "Lo que faltó: herramientas, tiempo, claridad, apoyo.",
        "Longed for": "Lo que echaste de menos y no tuvimos.",
      },
    },
    Sailboat: {
      name: "Velero",
      description: "Formato visual: qué impulsa al equipo y qué lo frena.",
      columns: {
        "Wind (helps us)": "Viento (nos impulsa)",
        "Anchors (slows us)": "Anclas (nos frenan)",
        "Rocks (risks)": "Rocas (riesgos)",
        "Island (goal)": "Isla (objetivo)",
      },
      hints: {
        "Wind (helps us)": "Lo que nos empujó hacia delante y nos dio velocidad.",
        "Anchors (slows us)": "Lo que nos lastró y nos frenó.",
        "Rocks (risks)": "Peligros por delante que pueden dañarnos si los ignoramos.",
        "Island (goal)": "Adónde queremos llegar de verdad.",
      },
    },
    KALM: {
      name: "KALM",
      description: "Ordena las prácticas según mantener, añadir, reducir o ampliar.",
      columns: {
        Keep: "Mantener",
        Add: "Añadir",
        "Less of": "Menos de",
        "More of": "Más de",
      },
      hints: {
        "Keep": "Funciona tal cual: protégelo de cambios.",
        "Add": "Algo nuevo que merece la pena probar.",
        "Less of": "Vale la pena, pero nos estamos pasando.",
        "More of": "Funciona bien: nos vendría bien más.",
      },
    },
    Starfish: {
      name: "Estrella de mar",
      description:
        "Más detallado que Empezar/Parar/Continuar; ideal para equipos maduros.",
      columns: {
        "Keep doing": "Seguir haciendo",
        "More of": "Hacer más",
        "Less of": "Hacer menos",
        "Start doing": "Empezar a hacer",
        "Stop doing": "Dejar de hacer",
      },
      hints: {
        "Keep doing": "Eficaz tal como está: no lo toques.",
        "More of": "Ya ayuda; ganaríamos haciéndolo más.",
        "Less of": "No es dañino, pero hay demasiado.",
        "Start doing": "Una práctica nueva que deberíamos probar.",
        "Stop doing": "No aporta valor, o directamente estorba.",
      },
    },
  },
};
